/**
 * warehouseStatus — Alpine factory dla live status "Otwarte / Zamknięte" magazynów.
 *
 * Liczone w przeglądarce żeby działało niezależnie od LiteSpeed Cache strony produktu.
 * Strefa wymuszona na Europe/Warsaw (klient z zagranicznej strefy widzi polski czas).
 *
 * Użycie z PHP:
 *   <div x-data='warehouseStatus(<?php echo esc_attr( wp_json_encode([
 *           "weekday" => get_warehouse_hours("szczecin")["weekday"],
 *        ]) ); ?>)'>
 *       <span x-text="statusLabel"></span>
 *       <span x-text="nextOpenLabel"></span>
 *   </div>
 *
 * Wejście: { weekday: ['09:00','18:00'] | null, weekend: ['09:00','14:00'] | null }
 * Eksponowane na elemencie: open (bool), statusLabel (string), nextOpenLabel (string).
 */

// Skróty Intl 'en-GB' → indeks 0..6 (0 = poniedziałek, 6 = niedziela)
const WEEKDAY_INDEX = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };
const DAY_NAMES_PL = ['poniedziałek', 'wtorek', 'środa', 'czwartek', 'piątek', 'sobota', 'niedziela'];

function toMinutes(hhmm) {
    const [h, m] = hhmm.split(':').map(Number);
    return h * 60 + m;
}

// "09:00" → "9:00" (UI po polsku, bez zera wiodącego dla godziny)
function formatHourPl(hhmm) {
    const [h, m] = hhmm.split(':');
    return `${parseInt(h, 10)}:${m}`;
}

// Aktualny moment w strefie Europe/Warsaw — bez tworzenia obiektu Date pośredniego
function nowInWarsaw() {
    const fmt = new Intl.DateTimeFormat('en-GB', {
        timeZone: 'Europe/Warsaw',
        weekday: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
    });
    const parts = fmt.formatToParts(new Date());
    const weekday = parts.find((p) => p.type === 'weekday')?.value || 'Mon';
    const hour = parseInt(parts.find((p) => p.type === 'hour')?.value || '0', 10);
    const minute = parseInt(parts.find((p) => p.type === 'minute')?.value || '0', 10);
    return {
        dayIdx: WEEKDAY_INDEX[weekday] ?? 0,
        minutes: hour * 60 + minute,
    };
}

export const warehouseStatus = (config = {}) => ({
    open: false,
    statusLabel: 'Sprawdzam…',
    nextOpenLabel: '',
    _timer: null,

    init() {
        this.compute();
        // odśwież co minutę — żeby status przeskoczył po godzinie zamknięcia
        // gdy klient zostawi kartę produktu otwartą
        this._timer = setInterval(() => this.compute(), 60_000);
    },

    destroy() {
        if (this._timer) clearInterval(this._timer);
    },

    compute() {
        const weekday = config.weekday || null;
        const weekend = config.weekend || null;
        const { dayIdx, minutes } = nowInWarsaw();
        const isWeekday = dayIdx <= 4; // Mon..Fri
        const rangeToday = isWeekday ? weekday : weekend;

        if (rangeToday) {
            const [openStr, closeStr] = rangeToday;
            const openMin = toMinutes(openStr);
            const closeMin = toMinutes(closeStr);

            if (minutes >= openMin && minutes < closeMin) {
                this.open = true;
                this.statusLabel = 'Otwarte';
                this.nextOpenLabel = `do ${formatHourPl(closeStr)}`;
                return;
            }
            if (minutes < openMin) {
                this.open = false;
                this.statusLabel = 'Zamknięte';
                this.nextOpenLabel = `otwieramy o ${formatHourPl(openStr)}`;
                return;
            }
            // po godzinie zamknięcia → szukamy następnego dnia z godzinami
        }

        this.open = false;
        this.statusLabel = 'Zamknięte';
        this.nextOpenLabel = this._nextOpenSuffix(dayIdx, weekday, weekend);
    },

    _nextOpenSuffix(currentDayIdx, weekday, weekend) {
        for (let offset = 1; offset <= 7; offset++) {
            const nextIdx = (currentDayIdx + offset) % 7;
            const isNextWeekday = nextIdx <= 4;
            const range = isNextWeekday ? weekday : weekend;
            if (range) {
                const openLabel = formatHourPl(range[0]);
                if (offset === 1) {
                    return `otwieramy jutro o ${openLabel}`;
                }
                return `otwieramy w ${DAY_NAMES_PL[nextIdx]} o ${openLabel}`;
            }
        }
        return '';
    },
});
